/**
 * Initialize Google Tag Manager.
 *
 * GTM is the single integration point through which every customer-deployed
 * tracking tag (including Ricald's own pixel beacon) is published. The
 * Settings → Integrations → Connect GTM flow OAuth-links a customer's GTM
 * container and uses the server-side `gtm_service.create_pixel_tag` /
 * `create_version_and_publish` machinery to push the Ricald pixel as a
 * GTM tag — but only IF the customer's site already loads a GTM container.
 *
 * This module installs that container on the *Ricald-owned* site
 * (ricald.ai) so we dogfood our own customer-facing flow end-to-end. The
 * pixel beacon then arrives by the same path a paying customer's would,
 * via GTM, not via a hardcoded script tag.
 *
 * Required environment variable:
 * - VITE_GTM_CONTAINER_ID: GTM container ID for the Ricald-owned site
 *   (looks like ``GTM-XXXXXXX``). Find/create in
 *   https://tagmanager.google.com/ for the ricald.ai property.
 *
 * Empty / unset / placeholder values are silently skipped — local dev,
 * staging without a tracking container, and PR-preview deploys all
 * remain untouched.
 */
export function initGtm(): void {
  const containerId = import.meta.env.VITE_GTM_CONTAINER_ID;

  if (!containerId || !/^GTM-[A-Z0-9]+$/.test(containerId)) {
    if (import.meta.env.PROD && !containerId) {
      console.warn(
        'VITE_GTM_CONTAINER_ID not configured. ' +
        'Ricald pixel + any other GTM-deployed tags will not fire on this build.'
      );
    }
    return;
  }

  // Idempotent — bail if already loaded (StrictMode double-mount,
  // hot-reload during dev, navigation between Sentry-routed errors, etc).
  if ((window as unknown as Record<string, unknown>).__gtm_loaded__) {
    return;
  }
  (window as unknown as Record<string, unknown>).__gtm_loaded__ = true;

  // Google's official GTM snippet, translated from the inline-HTML form
  // to typed TypeScript so the bundle stays clean. Equivalent to:
  //   (function(w,d,s,l,i){...})(window,document,'script','dataLayer',containerId)
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const gtmScript = document.createElement('script');
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    containerId
  )}`;

  // The canonical GTM snippet inserts the script BEFORE the first existing
  // script tag, which loads GTM earliest. In SPA bundles (and in jsdom test
  // environments) there may not be any script tag yet, so we fall back to
  // appending into <head>. Equivalent in practice — both load before any
  // user-rendered content in production, since this runs in main.tsx
  // before React mounts.
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(gtmScript, firstScript);
  } else {
    document.head.appendChild(gtmScript);
  }
}
